import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector, useDispatch } from "react-redux";
import Script from "next/script";
import useDefaultCountry from "@/hooks/defaults/useDefaultCountry";
import { useSendApplicationMutation } from "@/modules/client/loan-application/store/services/endpoints/send-application";
import { setOrganization } from "@/modules/client/loan-application/store/reducers/organization-details-slice";
import BORROWER_LOAN_ORIGINATION from "@/modules/client/loan-application/events";
import loanApplicationValidation from "@/modules/client/loan-application/features/loan-origination/validations/loan-application-validation";
import useWizardNavigation from "@/modules/client/loan-application/features/loan-origination/hooks/use-wizard-navigation";
import WizardHeader from "@/modules/client/loan-application/features/loan-origination/sub-features/wizard-header/components/wizard-header";
import WizardStepper from "@/modules/client/loan-application/features/loan-origination/sub-features/wizard-stepper/components/wizard-stepper";
import WizardFooter from "@/modules/client/loan-application/features/loan-origination/sub-features/wizard-footer/components/wizard-footer";
import DealSummaryStep from "@/modules/client/loan-application/features/loan-origination/sub-features/deal-summary/components/deal-summary-step";
import BorrowerInfoStep from "@/modules/client/loan-application/features/loan-origination/sub-features/borrower-info/components/borrower-info-step";
import AdditionalDetailsStep from "@/modules/client/loan-application/features/loan-origination/sub-features/additional-details/components/additional-details-step";

const { APPLICATION_SUBMITTED } = BORROWER_LOAN_ORIGINATION;

export default function LoanOriginationWizard({ organizationId, organization }) {
  const dispatch = useDispatch();
  const [sendApplication, { isLoading }] = useSendApplicationMutation();
  const defaultCountry = useDefaultCountry();
  const { loanStatuses } = useSelector(state => state.loanStatuses);
  const defaultLoanStatus = loanStatuses.find(s => s.name === "Lead");
  const stepperRef = useRef(null);
  const submittingRef = useRef(false);
  const { currentStep, isFirstStep, isLastStep, goNext, goBack } = useWizardNavigation({ stepperRef });

  const defaultValues = useMemo(() => ({
    details: { countryId: defaultCountry?.countryId, loanStatusId: defaultLoanStatus?.parameterId },
    applicant: { isUsCitizen: true, hasBeenBankruptcy: false },
    coApplicants: [],
    referrer: {},
  }), [defaultCountry?.countryId, defaultLoanStatus?.parameterId]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(loanApplicationValidation),
  });

  const { reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (organization) dispatch(setOrganization(organization));
  }, [organization, dispatch]);

  const onSubmit = useCallback(async data => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const { details, applicant, coApplicants, referrer } = data;
      const token = await window.grecaptcha.enterprise.execute(process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY, { action: APPLICATION_SUBMITTED });
      const borrowers = [applicant, ...coApplicants];
      await sendApplication({
        organizationId, ...details, token, action: APPLICATION_SUBMITTED,
        referrerFirstName: referrer.firstName, referrerLastName: referrer.lastName, referrerEmail: referrer.email,
        borrowers,
      }).unwrap();
    } catch { /* handled by RTK Query + Bugsnag */ } finally {
      submittingRef.current = false;
    }
  }, [organizationId, sendApplication]);

  const handleNext = useCallback(() => goNext(methods), [goNext, methods]);
  const handleSubmit = useCallback(() => methods.handleSubmit(onSubmit)(), [methods, onSubmit]);

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case 0: return <DealSummaryStep />;
      case 1: return <BorrowerInfoStep />;
      case 2: return <AdditionalDetailsStep />;
      default: return null;
    }
  }, [currentStep]);

  return (
    <FormProvider {...methods}>
      <Script id="google-recaptcha" strategy="lazyOnload" src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY}`} />
      <div className="mx-auto flex w-full max-w-6xl min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-slate-900">
        <WizardHeader />
        <WizardStepper ref={stepperRef} onChange={() => {}} />
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">{stepContent}</div>
        <WizardFooter
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isSubmitting={isLoading}
          onBack={goBack}
          onNext={handleNext}
          onSubmit={handleSubmit}
        />
      </div>
      <p className="shrink-0 pt-3 text-center text-xs text-gray-400 dark:text-slate-500">
        Powered by Fundview 🚀
      </p>
    </FormProvider>
  );
}

LoanOriginationWizard.propTypes = {
  organizationId: PropTypes.string.isRequired,
  organization: PropTypes.shape({
    organizationName: PropTypes.string,
    logoUrl: PropTypes.string,
  }),
};

